import {
  Alert,
  AlertTitle,
  Box,
  Breadcrumbs,
  Button,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import { Fragment } from "react/jsx-runtime";
import { Breadcrumb } from "~/components/Breadcrumbs/Breadcrumbs";
import { ContentFlag } from "~/components/content-flag";
import { HomeIcon, SecurityPipelineIcon } from "~/icons";
import { FormatDate } from "~/lib/date";
import { capitalizeFirstLetter } from "~/lib/helpers";
import { Link as RouterLink } from "react-router-dom";
import { ScopeType, SpaceScope, useScope } from "~/hooks/useScope";
import { OrganizationScope } from "~/hooks/useScope";
import {
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { Flex } from "~/components/Flex";
import { GithubSecurityPipelineGetHelpDialog } from "~/pages/integrations/security-pipeline/security-pipeline-github/components/SecurityPipelineGithubGetHelp/GithubSecurityPipelineGetHelpDialog";
import { useDialogs } from "@toolpad/core";
import { SECURITY_PIPELINE_DISPLAY_NAME } from "../../../constants";
import { formatStatus } from "~/pages/integrations/hosted-integrations/utils/statuses";
import { useLoadOrganizationForSpaceQuery } from "~/operations";

interface SecurityPipelineGithubPageHeaderProps {
  integrationStatus: string | null | undefined;
  errorMsg: string | null | undefined;
  scope: SpaceScope | OrganizationScope;
  integrationName: string;
  lastUpdated: string | null | undefined;
  integrationId: string | null | undefined;
}

export const SecurityPipelineGithubPageHeader = ({
  integrationStatus,
  errorMsg,
  scope,
  integrationName,
  lastUpdated,
  integrationId,
}: SecurityPipelineGithubPageHeaderProps) => {
  const { spaceScope } = useScope();
  const { data: orgDataForSpace } = useLoadOrganizationForSpaceQuery({
    variables: { spaceMrn: spaceScope?.mrn ?? "" },
    skip: !spaceScope?.mrn,
  });
  const dialogs = useDialogs();
  const isSpaceScope = scope.type === ScopeType.Space;

  const spaceBreadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?${scope.params}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Link
      key="/space/integrations"
      component={RouterLink}
      to={`/space/integrations?${scope.params}`}
    >
      Integrations
    </Link>,
    <Link
      key="/space/integrations/security_pipeline_github"
      component={RouterLink}
      to={`/space/integrations/security_pipeline_github?${scope.params}`}
    >
      Github {SECURITY_PIPELINE_DISPLAY_NAME}
    </Link>,
    <Typography key={1} color="text.primary">
      {integrationName}
    </Typography>,
  ];

  const organizationBreadcrumbs = [
    <Link
      key="/organization/overview"
      component={RouterLink}
      to={`/organization/overview?${scope.params}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Typography key={1} color="text.primary">
      {integrationName}
    </Typography>,
  ];

  const handleGetHelp = () => {
    dialogs.open(GithubSecurityPipelineGetHelpDialog, {});
  };

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 5 }} separator="›">
        {isSpaceScope ? spaceBreadcrumbs : organizationBreadcrumbs}
      </Breadcrumbs>
      {/* Header Block */}
      <Grid
        container
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "inherit", sm: "flex-start" },
          justifyContent: "space-between",
          mb: 6.5,
        }}
      >
        {errorMsg && (
          <Grid sx={{ mb: 3 }} size={12}>
            <Alert severity="error" variant="outlined">
              <AlertTitle>Error</AlertTitle>
              {capitalizeFirstLetter(errorMsg)}
            </Alert>
          </Grid>
        )}
        <Grid
          container
          size={{
            xs: "grow",
            sm: 12,
            md: 8,
          }}
        >
          <Flex gap={3}>
            <SecurityPipelineIcon
              sx={{
                fontSize: 48,
                color: "text.secondary",
                verticalAlign: "middle",
              }}
            />
            <Flex flexDirection="column">
              <Grid
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                }}
              >
                <Flex gap={3}>
                  <Typography
                    variant="h1"
                    component="h1"
                    fontSize={32}
                    fontWeight={700}
                    sx={{
                      mr: 4,
                      textTransform: "uppercase",
                      lineHeight: "inherit",
                    }}
                  >
                    Security pipeline
                    {scope.type === ScopeType.Organization ? " setup" : ""}
                  </Typography>
                </Flex>
              </Grid>
              {integrationStatus && (
                <Grid sx={{ mb: 1 }} size={12}>
                  <ContentFlag
                    flag={formatStatus(integrationStatus)}
                    color={integrationStatus}
                  />
                </Grid>
              )}
              {integrationId && (
                <Grid container columnGap={2} mb={{ sm: 1, md: 0 }}>
                  <Grid
                    size={{
                      xs: 12,
                      sm: "auto",
                    }}
                  >
                    <AccessTimeIcon
                      sx={{
                        fontSize: 16,
                        height: 28,
                        color: "text.secondary",
                        verticalAlign: "middle",
                      }}
                    />
                    <Typography
                      display="inline"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 0.75 }}
                    >
                      {/* Last scanned: April 05, 10:10am */}
                      Last updated: <b>{FormatDate(lastUpdated)}</b>
                    </Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: "auto",
                    }}
                  >
                    <Fragment>
                      <PersonIcon
                        sx={{
                          fontSize: 16,
                          height: 28,
                          color: "text.secondary",
                          verticalAlign: "middle",
                        }}
                      />
                      <Typography
                        display="inline"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 0.75 }}
                      >
                        Created by: <b>Mondoo</b>
                      </Typography>
                    </Fragment>
                  </Grid>
                </Grid>
              )}
            </Flex>
          </Flex>
        </Grid>
        <Grid sx={{ mt: { xs: 2, sm: 0 }, alignSelf: "flex-start" }}>
          {scope.type === ScopeType.Organization ? (
            <Button
              variant="outlined"
              color="secondary"
              sx={{ ml: 2 }}
              onClick={handleGetHelp}
            >
              How It Works
            </Button>
          ) : (
            <Button
              component={RouterLink}
              to={`/organization/integrations/security_pipeline_github?organizationId=${orgDataForSpace?.space?.organization?.id}`}
              variant="outlined"
              color="secondary"
              sx={{ ml: 2 }}
            >
              Org setup
            </Button>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
